import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPromotionsDataView } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Img from "@insite/mobius/Img";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CENTaxTag from "@tCentro/Icons/CENTaxTag";
import CustomInfoIcon from "@tCentro/Icons/CustomInfoIcon";
import Tag from "@tCentro/MobiusOverrides/Tag";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.onePageCheckout;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    return {
        cart: cartState.value,
        isLoading: !cartState.value || cartState.isLoading,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        promotions: getCurrentPromotionsDataView(state).value,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface CenOnePageCheckoutCartItemsStyles {
    container?: InjectableCss;
    centeringWrapper?: InjectableCss;
    headingContainer?: InjectableCss;
    cartContainer?: InjectableCss;
    imgStyles?: InjectableCss;
    imgContainer?: InjectableCss;
    productTitleStyles?: TypographyPresentationProps;
    productInfoStyles?: TypographyPresentationProps;
    editLinkStyles?: InjectableCss;
    imgLinkStyles?: InjectableCss;
    tbdFreightMessage?: InjectableCss;
    infoIconStyles?: IconPresentationProps;
    iconStyle?: IconPresentationProps;
    promotionNameText?: TypographyProps;
    promotionWrapper?: InjectableCss;
    restrictedProductStyle?: TypographyProps;
}

export const cenOnePageCheckoutCartItemsStyles: CenOnePageCheckoutCartItemsStyles = {
    restrictedProductStyle: {
        css: css`
            color: ${getColor("danger.main")};
        `,
    },
    infoIconStyles: {
        size: 15,
        css: css`
            margin-top: 3px;
            svg {
                > path {
                    fill: ${getColor("text.link")};
                }
            }
            @media (min-width: 1024px) and (max-width: 1198px) {
                width: 14px;
                height: 9px;
            }
        `,
    },
    tbdFreightMessage: {
        css: css`
            font-size: 14px;
            line-height: 20px;
            color: ${getColor("text.link")};
            display: flex;
            align-items: start;
            gap: 5px;
            margin: 2px 0 0;
            @media (min-width: 1024px) and (max-width: 1198px) {
                font-size: 11px;
                line-height: 14px;
            }
        `,
    },
    imgLinkStyles: {
        css: css`
            width: 100%;
            height: 100%;
            > span {
                width: 100%;
                height: 100%;
            }
        `,
    },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 450px;
        `,
    },
    editLinkStyles: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-size: ${TYPOGRAPHY.size.tiny};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 16px;
        `,
    },
    productInfoStyles: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.base};
            line-height: 16px;
            color: ${getProp("theme.typography.p.color")};
        `,
    },
    productTitleStyles: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 16px;
            color: ${getProp("theme.typography.p.color")};
        `,
    },
    cartContainer: {
        css: css`
            display: flex;
            gap: 24px;
        `,
    },
    imgContainer: {
        css: css`
            width: 75px;
            height: 75px;
            aspect-ratio: 1/1;
            background-color: ${COLORS.white};
            box-shadow: 0px 2px 8px 0px rgba(38, 37, 37, 0.16);
        `,
    },
    imgStyles: {
        css: css`
            width: 75px;
            height: 75px;
            object-fit: contain;
        `,
    },
    container: {
        css: css`
            background-color: ${COLORS.background.grayShade};
            border-top: 2px solid #bdbbbd;
            padding: 25px 25px 16px 25px;
            display: flex;
            flex-direction: column;
            gap: 24px;
        `,
    },
    headingContainer: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: space-between;
            span {
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-size: ${TYPOGRAPHY.size.largest};
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                line-height: 32px;
                color: ${getProp("theme.typography.p.color")};
            }
        `,
    },
    iconStyle: {
        size: 14,
        css: css`
            margin-top: 3px;
            > svg {
                > path {
                    fill: #266b40;
                }
            }
        `,
    },
    promotionNameText: {
        css: css`
            font-size: 14px;
            line-height: 16px;
            font-weight: 600;
            color: #3ca865;
        `,
    },
    promotionWrapper: {
        css: css`
            display: flex;
            margin-top: 5px;
            align-items: center;
            flex-wrap: wrap;
            gap: 10px;
        `,
    },
};

const styles = cenOnePageCheckoutCartItemsStyles;

const CenOnePageCheckoutCartItems = ({ cart, isLoading, cartUrl, promotions }: Props) => {
    if (isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner data-test-selector="onePageCheckout_Loading_order_summary_cartItems" />
            </StyledWrapper>
        );
    }
    const productsCannotBePurchased = cart?.cartLines?.some(o => o.isRestricted || !o.isActive || o.cantBuy);
    const firstThreeCartItems = cart?.cartLines && cart?.cartLines.slice(0, 3);
    const nonQuoteRequiredItems = cart?.cartLines?.filter(item => !item.quoteRequired) || [];

    return (
        <StyledWrapper {...styles.container} data-test-selector="onePageCheckout_order_summary_cartItems">
            <StyledWrapper {...styles.headingContainer}>
                <Typography>{translate("My Cart")}</Typography>
                {nonQuoteRequiredItems && nonQuoteRequiredItems?.length > 0 && (
                    <Typography>
                        {nonQuoteRequiredItems?.length}{" "}
                        {nonQuoteRequiredItems.length > 1 ? translate("items") : translate("item")}
                    </Typography>
                )}
            </StyledWrapper>
            {nonQuoteRequiredItems?.map((item, index) => {
                const isShippingOverWeight = item?.properties?.isItemOverWeight === "true";
                return (
                    <StyledWrapper data-test-selector="cart_Container" key={index} {...styles.cartContainer}>
                        <StyledWrapper {...styles.imgContainer}>
                            <Link href={item?.productUri} {...styles.imgLinkStyles}>
                                <Img src={item.smallImagePath} altText={item.altText} {...styles.imgStyles} />
                            </Link>
                        </StyledWrapper>
                        <StyledWrapper
                            css={css`
                                display: flex;
                                flex-direction: column;
                                gap: 2px;
                            `}
                        >
                            <Link href={item?.productUri}>
                                <Typography as="p" {...styles.productTitleStyles}>
                                    {item?.shortDescription}
                                </Typography>
                            </Link>
                            <Typography {...styles.productInfoStyles} as="p">
                                {translate("QTY: ")}
                                {item?.qtyOrdered}
                            </Typography>
                            <Typography as="p" {...styles.productInfoStyles}>
                                {translate("Total: ")}
                                {item.pricing?.extendedActualPriceDisplay}
                            </Typography>
                            {productsCannotBePurchased && (
                                <Typography {...styles.restrictedProductStyle}>
                                    {siteMessage("Restricted Product")}
                                </Typography>
                            )}
                            {isShippingOverWeight && (
                                <Typography as="p" {...styles.tbdFreightMessage}>
                                    <Icon src={CustomInfoIcon} {...styles.infoIconStyles}></Icon>
                                    {siteMessage("Shipping_Item_OverWeight")}
                                </Typography>
                            )}
                            {promotions?.map(promotion => {
                                if (item.id !== promotion.orderLineId) {
                                    return null;
                                }
                                return (
                                    <StyledWrapper key={promotion.id} {...styles.promotionWrapper}>
                                        <div style={{ display: "inline-block" }}>
                                            <Tag
                                                isPromotion
                                                deletable={false}
                                                data-test-selector={`promotionCode_promotionTag_${promotion.promotionCode}`}
                                            >
                                                <Icon src={CENTaxTag} {...styles.iconStyle} />
                                                {promotion.promotionCode}
                                            </Tag>
                                        </div>

                                        <StyledWrapper
                                            key={promotion.id}
                                            data-test-selector="orderPromotionName"
                                            {...styles.promotionNameText}
                                        >
                                            {promotion.name}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                );
                            })}
                        </StyledWrapper>
                    </StyledWrapper>
                );
            })}
            <StyledWrapper
                css={css`
                    display: flex;
                    width: 100%;
                    justify-content: flex-end;
                `}
            >
                <Link {...styles.editLinkStyles} href={cartUrl}>
                    {translate("Edit Cart")}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CenOnePageCheckoutCartItems),
    definition: {
        group: "One Page Checkout",
        allowedContexts: ["OnePageCheckoutPage"],
        displayName: "Order Summary Cart Items",
    },
};

export default widgetModule;
